// src/pages/About/index.js
import React, { useState, useRef, useMemo } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import AuthGuard from '../../components/AuthGuard';
import { 
  UserOutlined, 
  MailOutlined, 
  CodeOutlined, 
  HeartOutlined,
  EditOutlined,
  CalendarOutlined,
  SaveOutlined,
  CloseOutlined,
  CameraOutlined,
  IdcardOutlined
} from '@ant-design/icons';
import './index.css';

const About = () => {
  const { user, updateUser, addTechStack, removeTechStack } = useAuth();
  const [isEditing, setIsEditing] = useState(false);
  const [newTech, setNewTech] = useState('');
  const [avatarError, setAvatarError] = useState(false);
  const [isLocalImage, setIsLocalImage] = useState(false);
  const fileInputRef = useRef(null);
  
  // 本地状态用于编辑
  const [editForm, setEditForm] = useState({
    username: '',
    bio: '',
    detailedIntro: '',
    email: '',
    hobbies: ''
  });

  // 初始化编辑表单
  React.useEffect(() => {
    if (user) {
      setEditForm({
        username: user.username || '',
        bio: user.bio || '',
        detailedIntro: user.detailedIntro || '',
        email: user.email || '',
        hobbies: user.hobbies || ''
      });
    }
  }, [user]);

  // 使用useMemo确保techStack总是数组
  const techStack = useMemo(() => {
    if (!user || !user.techStack) return [];
    if (Array.isArray(user.techStack)) return user.techStack;
    if (typeof user.techStack === 'string') {
      return user.techStack.split(',').map(item => item.trim()).filter(Boolean);
    }
    return [];
  }, [user]);

  const hobbiesArray = useMemo(() => {
    if (!user || !user.hobbies) return [];
    if (Array.isArray(user.hobbies)) return user.hobbies;
    if (typeof user.hobbies === 'string') {
      return user.hobbies.split(',').map(item => item.trim()).filter(Boolean);
    }
    return [];
  }, [user]);

  // 处理表单输入变化
  const handleInputChange = (field, value) => {
    setEditForm(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // 添加技术栈
  const handleAddTech = () => {
    if (newTech.trim()) {
      addTechStack(newTech.trim());
      setNewTech('');
    }
  };

  // 删除技术栈
  const handleRemoveTech = (index) => {
    removeTechStack(index);
  };

  // 保存修改
  const handleSave = () => {
    // 更新所有字段
    updateUser(editForm);
    setIsEditing(false);
  };

  // 开始编辑
  const handleEdit = () => {
    setIsEditing(true);
  };

  // 取消编辑
  const handleCancel = () => {
    // 重置表单到原始数据
    setEditForm({
      username: user.username || '',
      bio: user.bio || '',
      detailedIntro: user.detailedIntro || '',
      email: user.email || '',
      hobbies: user.hobbies || ''
    });
    setNewTech('');
    setAvatarError(false);
    setIsLocalImage(false);
    setIsEditing(false);
  };

  // 处理图片加载错误
  const handleImageError = () => {
    setAvatarError(true);
  };

  // 处理文件选择
  const handleFileSelect = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (!file.type.match('image.*')) {
        alert('请选择图片文件（jpg、png、gif等）');
        return;
      }
      
      if (file.size > 5 * 1024 * 1024) {
        alert('图片大小不能超过5MB');
        return;
      }
      
      const reader = new FileReader();
      reader.onload = (event) => {
        setIsLocalImage(true);
        updateUser({ avatarUrl: event.target.result });
      };
      reader.readAsDataURL(file);
      
      e.target.value = '';
    }
  };

  // 触发文件选择
  const triggerFileInput = () => {
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // 获取头像显示URL
  const getAvatarUrl = () => {
    if (avatarError || !user?.avatarUrl) {
      return `https://ui-avatars.com/api/?name=${encodeURIComponent(user?.username || 'User')}&background=4361ee&color=fff&size=150`;
    }
    return user.avatarUrl;
  };

  // 清除本地图片
  const clearLocalImage = () => {
    updateUser({ avatarUrl: '' });
    setIsLocalImage(false);
  };

  return (
    <AuthGuard>
      <div className="about-page">
        <div className="about-container">
          {/* 页面标题 */}
          <div className="page-header">
            <h1 className="page-title">
              {isEditing ? '编辑个人信息' : '个人主页'}
            </h1>
            <p className="page-subtitle">
              {isEditing ? '完善您的个人信息，让其他人更好地了解您' : '这是我的个人空间，欢迎来访！'}
            </p>
          </div>

          {/* 主卡片 */}
          <div className="profile-card">
            {/* 头部区域 */}
            <div className="profile-header">
              {/* 头像区域 */}
              <div className="avatar-section">
                <div className="avatar-wrapper" onClick={isEditing ? triggerFileInput : null}>
                  <img 
                    src={getAvatarUrl()} 
                    alt="头像" 
                    className={`profile-avatar ${isEditing ? 'editable' : ''}`}
                    onError={handleImageError}
                  />
                  {isEditing && (
                    <div className="avatar-edit-overlay">
                      <CameraOutlined />
                      <span>更换头像</span>
                    </div>
                  )}
                </div>
                
                <div className="avatar-info">
                  {/* 用户名/昵称 - 现在可以编辑了 */}
                  {isEditing ? (
                    <div className="username-edit">
                      <div className="edit-field-group">
                        <label className="edit-label">
                          <IdcardOutlined /> 用户名
                        </label>
                        <input
                          type="text"
                          value={editForm.username}
                          onChange={(e) => handleInputChange('username', e.target.value)}
                          placeholder="请输入用户名"
                          className="username-input"
                        />
                      </div>
                    </div>
                  ) : (
                    <h2 className="username">{user?.username}</h2>
                  )}
                  
                  {/* 邮箱 - 只读，因为通常不建议修改邮箱 */}
                  <p className="user-email">
                    <MailOutlined /> {user?.email}
                  </p>
                  
                  <div className="user-meta">
                    <span className="meta-item">
                      <CalendarOutlined /> 加入时间：{user?.createdAt || '2024-01-01'}
                    </span>
                  </div>
                </div>
              </div>

              {/* 编辑按钮 */}
              {!isEditing && (
                <button className="edit-profile-btn" onClick={handleEdit}>
                  <EditOutlined /> 编辑信息
                </button>
              )}
            </div>

            {/* 内容区域 */}
            <div className="profile-content">
              {/* 个人简介 */}
              <div className="info-section">
                <h3 className="section-title">
                  <UserOutlined /> 个人简介
                </h3>
                {isEditing ? (
                  <div className="edit-field-group">
                    <textarea
                      value={editForm.bio}
                      onChange={(e) => handleInputChange('bio', e.target.value)}
                      placeholder="介绍一下自己吧..."
                      className="edit-textarea"
                      rows="3"
                    />
                    <div className="edit-hint">简短介绍自己，最多200字</div>
                  </div>
                ) : (
                  <p className="info-content">{user?.bio || '这个人很神秘，什么也没留下。'}</p>
                )}
              </div>

              {/* 详细介绍 */}
              <div className="info-section">
                <h3 className="section-title">详细介绍</h3>
                {isEditing ? (
                  <div className="edit-field-group">
                    <textarea
                      value={editForm.detailedIntro}
                      onChange={(e) => handleInputChange('detailedIntro', e.target.value)}
                      placeholder="分享更多关于你的故事..."
                      className="edit-textarea"
                      rows="4"
                    />
                    <div className="edit-hint">可以分享你的经历、兴趣、技能等</div>
                  </div>
                ) : (
                  <p className="info-content">{user?.detailedIntro || '欢迎来到我的博客！请登陆后完善您的个人信息！'}</p>
                )}
              </div>

              {/* 技术栈 */}
              <div className="info-section">
                <h3 className="section-title">
                  <CodeOutlined /> 技术栈
                </h3>
                {isEditing ? (
                  <div className="edit-section">
                    <div className="tags-container">
                      {techStack.map((tech, index) => (
                        <div key={index} className="tag-item edit">
                          <span>{tech}</span>
                          <button
                            type="button"
                            className="tag-remove"
                            onClick={() => handleRemoveTech(index)}
                          >
                            ×
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="tag-input-group">
                      <input
                        type="text"
                        value={newTech}
                        onChange={(e) => setNewTech(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddTech();
                          }
                        }}
                        placeholder="输入技术栈（如：React、Python、Node.js）"
                        className="tag-input"
                      />
                      <button 
                        type="button" 
                        className="tag-add-btn"
                        onClick={handleAddTech}
                      >
                        添加
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="tags-container">
                    {techStack.length > 0 ? (
                      techStack.map((tech, index) => (
                        <span key={index} className="tag-item">
                          {tech}
                        </span>
                      ))
                    ) : (
                      <p className="empty-state">暂无技术栈</p>
                    )}
                  </div>
                )}
              </div>

              {/* 兴趣爱好 */}
              <div className="info-section">
                <h3 className="section-title">
                  <HeartOutlined /> 兴趣爱好
                </h3>
                {isEditing ? (
                  <div className="edit-field-group">
                    <input
                      type="text"
                      value={editForm.hobbies}
                      onChange={(e) => handleInputChange('hobbies', e.target.value)}
                      placeholder="请输入兴趣爱好（用逗号分隔，如：阅读、旅行、摄影）"
                      className="edit-input"
                    />
                    <div className="edit-hint">多个爱好用逗号分隔</div>
                  </div>
                ) : (
                  <div className="tags-container">
                    {hobbiesArray.length > 0 ? (
                      hobbiesArray.map((hobby, index) => (
                        <span key={index} className="tag-item hobby">
                          {hobby}
                        </span>
                      ))
                    ) : (
                      <p className="empty-state">暂无兴趣爱好</p>
                    )}
                  </div>
                )}
              </div>

              {/* 邮箱信息 - 在编辑模式下显示 */}
              {isEditing && (
                <div className="info-section">
                  <h3 className="section-title">
                    <MailOutlined /> 邮箱
                  </h3>
                  <div className="edit-field-group">
                    <input
                      type="email"
                      value={editForm.email}
                      onChange={(e) => handleInputChange('email', e.target.value)}
                      placeholder="请输入邮箱"
                      className="edit-input"
                    />
                    <div className="edit-hint">邮箱用于登录和接收通知</div>
                  </div>
                </div>
              )}
            </div>

            {/* 编辑模式下的按钮 */}
            {isEditing && (
              <div className="edit-actions">
                <button 
                  type="button" 
                  className="action-btn save-btn"
                  onClick={handleSave}
                  disabled={!editForm.username.trim()} // 用户名不能为空
                >
                  <SaveOutlined /> 保存修改
                </button>
                <button 
                  type="button" 
                  className="action-btn cancel-btn"
                  onClick={handleCancel}
                >
                  <CloseOutlined /> 取消编辑
                </button>
              </div>
            )}
          </div>

          {/* 编辑提示 */}
          {isEditing && (
            <div className="edit-tips">
              <h3>💡 编辑提示：</h3>
              <ul>
                <li>点击头像可以上传新的头像（支持jpg、png、gif格式，最大5MB）</li>
                <li>用户名是必填项，建议使用2-20个字符</li>
                <li>技术栈可以添加多个，点击标签上的×可以删除</li>
                <li>邮箱修改后会影响登录，请谨慎操作</li>
              </ul>
            </div>
          )}

          {/* 文件输入（隐藏） */}
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileSelect}
            accept="image/*"
            style={{ display: 'none' }}
          />
        </div>
      </div>
    </AuthGuard>
  );
};

export default About;